import React, { useState, useEffect } from 'react';
import Desktop from './components/Desktop';
import Taskbar from './components/Taskbar';
import StartMenu from './components/StartMenu';
import Window from './components/Window';
import MobileMenuButton from './components/MobileMenuButton';
import WindowsFrame from './components/WindowsFrame';
import TopBar from './components/TopBar';
import { useWindowManager } from './hooks/useWindowManager';
import { useClock } from './hooks/useClock';

const App = () => {
  const {
    openWindows,
    minimizedWindows,
    focusedWindow,
    openWindow,
    closeWindow,
    minimizeWindow,
    restoreWindow,
    maximizeWindow,
    focusWindow,
    draggedWindow,
    dragOffset,
    startDrag,
    drag,
    endDrag
  } = useWindowManager();

  const { time, date } = useClock();
  const [startMenuOpen, setStartMenuOpen] = useState(false);

  const toggleStartMenu = () => {
    setStartMenuOpen(!startMenuOpen);
  };

  const handleClickOutside = (event) => {
    const startMenu = document.getElementById('start-menu');
    const startButton = document.querySelector('.start-button');
    const mobileMenuBtn = document.querySelector('.mobile-menu-btn');
    
    if (startMenuOpen && 
        !startMenu?.contains(event.target) && 
        !startButton?.contains(event.target) && 
        !mobileMenuBtn?.contains(event.target)) {
      setStartMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('mousemove', drag);
    document.addEventListener('touchmove', drag, { passive: false });
    document.addEventListener('mouseup', endDrag);
    document.addEventListener('touchend', endDrag);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && startMenuOpen) {
        setStartMenuOpen(false);
      }
    });

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('mousemove', drag);
      document.removeEventListener('touchmove', drag);
      document.removeEventListener('mouseup', endDrag);
      document.removeEventListener('touchend', endDrag);
    };
  }, [startMenuOpen, drag, endDrag]);

  const windowData = [
    {
      id: 'about',
      title: 'About Trafalgar',
      icon: '📋',
      position: { top: 50, left: 50, width: 600, height: 500 },
      content: (
        <div className="content-section">
          <h2>Welcome to Trafalgar</h2>
          <p>Trafalgar is pioneering the future of human-computer interaction through innovative orchestration systems and cutting-edge technology solutions.</p>
          
          <h3>Our Mission</h3>
          <p>We develop quality products that solve significant problems, focusing on:</p>
          <ul>
            <li>Data sovereignty and security</li>
            <li>Accessible technology design</li>
            <li>AI-powered workflow automation</li>
            <li>Ethical foundation in all products</li>
          </ul>

          <h3>Company Goals</h3>
          <p>Our vision extends beyond dental speech-to-text to encompass:</p>
          <ul>
            <li>Medical and enterprise solutions</li>
            <li>Consumer technology products</li>
            <li>Advanced R&D in emerging technologies</li>
            <li>Positive impact across Australia and Asia</li>
          </ul>
        </div>
      )
    },
    {
      id: 'oscp',
      title: 'OSCP - Orchestration System Communication Protocol',
      icon: '⚙️',
      position: { top: 80, left: 100, width: 700, height: 600 },
      content: (
        <div className="content-section">
          <h2>What is OSCP?</h2>
          <p>The Orchestration System Communication Protocol (OSCP) is a lightweight, JSON-based communication protocol that acts as the foundation for the Orchestrator.</p>
          
          <h3>Key Features</h3>
          <ul>
            <li>Merges clinical psychology's memory principles with structured data representation</li>
            <li>Computer, model and AI agnostic design</li>
            <li>Secure, modular architecture</li>
            <li>Enables seamless information exchange between humans, AI, and software</li>
          </ul>

          <h3>The Orchestrator</h3>
          <p>Central middleware component acting as the "brain" of the system:</p>
          <ul>
            <li>Enables communication between humans, machines, and AI systems</li>
            <li>Manages memory, workflows, and plugin lifecycles</li>
            <li>LLM-agnostic integration capabilities</li>
            <li>Designed for local, secure operation</li>
          </ul>
        </div>
      )
    },
    {
      id: 'products',
      title: 'Trafalgar Products & Solutions',
      icon: '🚀',
      position: { top: 60, left: 80, width: 650, height: 550 },
      content: (
        <div className="content-section">
          <h2>Current Products</h2>
          
          <h3>Speech-to-Text Engine</h3>
          <p>Advanced speech recognition system specifically designed for dental and orthodontic workflows, integrating seamlessly with practice management systems.</p>

          <h3>Enterprise Workflow Integration</h3>
          <p>Comprehensive workflow automation solutions for businesses of all sizes, powered by OSCP technology.</p>

          <h2>Future Developments</h2>
        </div>
      )
    },
    {
      id: 'contact',
      title: 'Contact Trafalgar',
      icon: '📧',
      position: { top: 100, left: 120, width: 500, height: 400 },
      content: (
        <div className="content-section">
          <h2>Get in Touch</h2>
          <p>Ready to explore how Trafalgar's orchestration systems can transform your workflow?</p>
          
          <h3>Business Inquiries</h3>
          <p>For enterprise solutions and partnership opportunities, reach out to discuss how OSCP can revolutionize your operations.</p>

          <h3>Technical Consultation</h3>
          <p>Our team of experts in computer science, psychology, and design are ready to help you implement cutting-edge solutions.</p>

          <h3>Research & Development</h3>
          <p>Interested in collaborating on the future of human-computer interaction? Let's explore the possibilities together.</p>

          <div style={{ marginTop: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
            <p><strong>Contact Information:</strong></p>
            <p>Email: connor@trafalgarprojects.com</p>
            <p>Location: Australia</p>
            <p>Focus Areas: Medical, Enterprise, Consumer Technology</p>
          </div>
        </div>
      )
    }
  ];

  return (
    <WindowsFrame>
      <div className="app">
        <TopBar />
        <MobileMenuButton onClick={toggleStartMenu} />
        
        <Desktop 
          windowData={windowData}
          openWindow={openWindow}
          openWindows={openWindows}
          focusWindow={focusWindow}
        />

        {windowData.map(window => (
          <Window
            key={window.id}
            id={window.id}
            title={window.title}
            content={window.content}
            position={window.position}
            isOpen={openWindows.includes(window.id)}
            isFocused={focusedWindow === window.id}
            onClose={() => closeWindow(window.id)}
            onMinimize={() => minimizeWindow(window.id)}
            onMaximize={() => maximizeWindow(window.id)}
            onFocus={() => focusWindow(window.id)}
            onStartDrag={startDrag}
          />
        ))}

        <Taskbar
          openWindows={openWindows}
          minimizedWindows={minimizedWindows}
          focusedWindow={focusedWindow}
          onWindowClick={focusWindow}
          onStartMenuClick={toggleStartMenu}
          time={time}
          date={date}
        />

        <StartMenu
          isOpen={startMenuOpen}
          windowData={windowData}
          onWindowClick={(windowId) => {
            openWindow(windowId);
            setStartMenuOpen(false);
          }}
        />
      </div>
    </WindowsFrame>
  );
};

export default App;
